import React, { useMemo, useState } from 'react';
import CustomLink from '../UIElements/CustomLink';

/**
 * AIChatMarkdown
 * Renders the Markdown Insight AI answers in: paragraphs, headings, (nested) lists, tables,
 * quotes, code, bold, italic, links and Help Centre images. Builds React elements rather than HTML, so
 * nothing in an answer can inject markup. It's re-run on every streamed chunk, so it
 * tolerates half-written Markdown (an unclosed ** stays as text until it closes).
 * @param {Function} resolveFileLink takes a link to a file the model made (a non-web URL) and
 *   returns what clicking it does, or null to show only the link text
 * @param {Function} onImageLoad called when a Help Centre image in the answer finishes loading
 * @param {Function} onHelpLinkClick called when a link to a Help Centre article is clicked
 */

const FENCE = /^\s*```/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const HR = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const QUOTE = /^\s*>\s?(.*)$/;
const LIST_ITEM = /^(\s*)([-*+•]|\d+[.)])\s+(.*)$/;
// a line holding only images, e.g. a step photo copied in right under its numbered step
const IMAGE_LINE = /^\s*(!\[[^\]\n]*\]\([^)\s]+\)\s*)+$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$|^\s*\|\s*:?-+:?\s*\|\s*$/;

const isTableStart = (lines, i) => lines[i].includes('|') && i + 1 < lines.length && TABLE_DIVIDER.test(lines[i + 1]);

const startsBlock = (lines, i) => {
    const line = lines[i];
    return FENCE.test(line) || HEADING.test(line) || HR.test(line) || QUOTE.test(line)
        || LIST_ITEM.test(line) || isTableStart(lines, i);
};

const splitRow = line => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|')
    .map(cell => cell.trim());

// flat items with their indentation -> a tree of items with `children`
const nestListItems = flatItems => {
    const root = { indent: -1, children: [] };
    const stack = [root];
    flatItems.forEach(item => {
        while (stack.length > 1 && item.indent <= stack[stack.length - 1].indent) stack.pop();
        const node = { ...item, children: [] };
        stack[stack.length - 1].children.push(node);
        stack.push(node);
    });
    return root.children;
};

export const parseBlocks = source => {
    const lines = (source || '').replace(/\r\n?/g, '\n').split('\n');
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const heading = HEADING.exec(line);

        if (!line.trim()) {
            i += 1;
        }
        else if (FENCE.test(line)) {
            const code = [];
            i += 1;
            while (i < lines.length && !FENCE.test(lines[i])) {
                code.push(lines[i]);
                i += 1;
            }
            i += 1; // the closing fence, which may not have streamed in yet
            blocks.push({ type: 'code', text: code.join('\n') });
        }
        else if (heading) {
            blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
            i += 1;
        }
        else if (HR.test(line)) {
            blocks.push({ type: 'hr' });
            i += 1;
        }
        else if (isTableStart(lines, i)) {
            const header = splitRow(line);
            const rows = [];
            i += 2;
            while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
                rows.push(splitRow(lines[i]));
                i += 1;
            }
            blocks.push({ type: 'table', header, rows });
        }
        else if (QUOTE.test(line)) {
            const quoted = [];
            while (i < lines.length && QUOTE.test(lines[i])) {
                quoted.push(QUOTE.exec(lines[i])[1]);
                i += 1;
            }
            blocks.push({ type: 'quote', blocks: parseBlocks(quoted.join('\n')) });
        }
        else if (LIST_ITEM.test(line)) {
            const items = [];
            while (i < lines.length) {
                const current = lines[i];
                const match = LIST_ITEM.exec(current);
                if (match) {
                    items.push({
                        indent: match[1].length,
                        ordered: /\d/.test(match[2]),
                        start: parseInt(match[2], 10),
                        text: match[3],
                    });
                    i += 1;
                }
                else if (!current.trim()) {
                    // a blank line only ends the list if what follows isn't part of it
                    const next = lines[i + 1];
                    if (next === undefined || !(LIST_ITEM.test(next) || /^\s{2,}\S/.test(next))) break;
                    i += 1;
                }
                else if (/^\s+\S/.test(current)) {
                    // an indented line continues the item above it
                    items[items.length - 1].text += `\n${current.trim()}`;
                    i += 1;
                }
                else if (IMAGE_LINE.test(current)) {
                    // so does an image straight under it: it illustrates that step
                    items[items.length - 1].text += `\n${current.trim()}`;
                    i += 1;
                }
                else {
                    break;
                }
            }
            blocks.push({ type: 'list', items: nestListItems(items) });
        }
        else {
            const paragraph = [];
            do {
                paragraph.push(lines[i]);
                i += 1;
            } while (i < lines.length && lines[i].trim() && !startsBlock(lines, i));
            blocks.push({ type: 'paragraph', text: paragraph.join('\n') });
        }
    }

    return blocks;
};

// `code`, **bold**, *italic*, [text](url), ![alt](src)
const INLINE = new RegExp([
    /(`[^`\n]+`)/.source,
    /(\*\*(?=\S)[^*\n]*?\S\*\*)/.source,
    /(\*(?=[^*\s])[^*\n]*?[^*\s]\*|\*[^*\s]\*)/.source,
    /(\[([^\]\n]+)\]\(([^)\s]+)\))/.source,
    /(!\[([^\]\n]*)\]\(([^)\s]+)\))/.source,
].join('|'), 'g');

const isWebUrl = url => /^https?:\/\//i.test(url);

// a Help Centre page in this app, e.g. /help/zelp-sense-device/understanding-the-status-led
const isHelpLink = url => /^\/help(\/[A-Za-z0-9-]+)*\/?$/.test(url);
// anything else under /help (an anchor, a file) is shown as text, never treated as a chart file
const isUnderHelp = url => /^\/help([/#?]|$)/.test(url);

// a Help Centre image served from this app's own public/help folder. Any other image
// (another site, the model's sandbox, a page rather than a file) isn't shown at all.
const isHelpImage = url => /^\/help(\/[A-Za-z0-9_-]+)+\.(png|gif|jpe?g|webp)$/.test(url);

// An image that fails to load is hidden rather than shown as a broken icon. An empty alt
// marks a step photo as decorative, as the Help Centre does.
const HelpImage = ({ src, alt, onLoad }) => {
    const [failed, setFailed] = useState(false);
    if (failed) return null;
    return (
        <img
            className="ai-chat_markdown_image"
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={onLoad}
            onError={() => setFailed(true)} />
    );
};

const withLineBreaks = (text, key) => text.split('\n').map((part, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={`${key}-${index}`}>
        {index > 0 && <br />}
        {part}
    </React.Fragment>
));

export const renderInline = (text, { resolveFileLink, onImageLoad, onHelpLinkClick } = {}) => {
    const nodes = [];
    let last = 0;
    let match;
    INLINE.lastIndex = 0;

    // eslint-disable-next-line no-cond-assign
    while ((match = INLINE.exec(text)) !== null) {
        const key = match.index;
        if (match.index > last) nodes.push(withLineBreaks(text.slice(last, match.index), `t${key}`));
        const [token] = match;
        if (match[1]) nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
        else if (match[2]) nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
        else if (match[3]) nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
        else if (match[7]) {
            if (isHelpImage(match[9])) {
                nodes.push(<HelpImage key={key} src={match[9]} alt={match[8]} onLoad={onImageLoad} />);
            }
        }
        else if (isUnderHelp(match[6])) {
            nodes.push(isHelpLink(match[6])
                ? <CustomLink key={key} to={match[6]} isInline onClick={onHelpLinkClick}>{match[5]}</CustomLink>
                : <React.Fragment key={key}>{match[5]}</React.Fragment>);
        }
        else if (!isWebUrl(match[6])) {
            // e.g. `sandbox:/mnt/data/chart.png`, a file in the model's own sandbox. It links
            // to the chart it names once that arrives; until then (or if it never does) it's text.
            const openFile = resolveFileLink?.(match[6]);
            nodes.push(openFile ? (
                <CustomLink key={key} type="button" isInline onClick={openFile}>{match[5]}</CustomLink>
            ) : <React.Fragment key={key}>{match[5]}</React.Fragment>);
        }
        else {
            nodes.push(<CustomLink key={key} to={match[6]} isExternal isInline>{match[5]}</CustomLink>);
        }
        last = match.index + token.length;
    }
    if (last < text.length) nodes.push(withLineBreaks(text.slice(last), `t${last}`));
    return nodes;
};

const renderList = (items, key, options) => {
    const ordered = items[0]?.ordered;
    const Tag = ordered ? 'ol' : 'ul';
    const start = ordered && items[0].start !== 1 ? items[0].start : undefined;
    return (
        <Tag key={key} start={start}>
            {items.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>
                    {renderInline(item.text, options)}
                    {item.children.length ? renderList(item.children, 'nested', options) : null}
                </li>
            ))}
        </Tag>
    );
};

const renderBlock = (block, key, options) => {
    switch (block.type) {
        case 'heading':
            return block.level <= 2
                ? <h3 key={key}>{renderInline(block.text, options)}</h3>
                : <h4 key={key}>{renderInline(block.text, options)}</h4>;
        case 'code':
            return <pre key={key}><code>{block.text}</code></pre>;
        case 'hr':
            return <hr key={key} />;
        case 'quote':
            return (
                <blockquote key={key}>
                    {block.blocks.map((quoted, index) => renderBlock(quoted, index, options))}
                </blockquote>
            );
        case 'list':
            return renderList(block.items, key, options);
        case 'table':
            return (
                <div key={key} className="ai-chat_markdown_table">
                    <table>
                        <thead>
                            <tr>
                                {block.header.map((cell, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <th key={index}>{renderInline(cell, options)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {block.rows.map((row, rowIndex) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={rowIndex}>
                                    {block.header.map((_, index) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <td key={index}>{renderInline(row[index] || '', options)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        default:
            return <p key={key}>{renderInline(block.text, options)}</p>;
    }
};

const AIChatMarkdown = ({
    text, resolveFileLink, onImageLoad, onHelpLinkClick
}) => {
    const blocks = useMemo(() => parseBlocks(text), [text]);
    const options = { resolveFileLink, onImageLoad, onHelpLinkClick };
    return (
        <div className="ai-chat_markdown">
            {blocks.map((block, index) => renderBlock(block, index, options))}
        </div>
    );
};

export default AIChatMarkdown;
